// ─────────────────────────────────────────────────────────────────────────
// Hearth — small pure helpers shared across the UI.
// ─────────────────────────────────────────────────────────────────────────
import type { AbilityKey } from "./types";

export const ABILITY_NAMES: Record<AbilityKey, string> = {
  str: "Strength",
  dex: "Dexterity",
  con: "Constitution",
  int: "Intelligence",
  wis: "Wisdom",
  cha: "Charisma",
};
export const ABILITY_ABBR: Record<AbilityKey, string> = {
  str: "STR",
  dex: "DEX",
  con: "CON",
  int: "INT",
  wis: "WIS",
  cha: "CHA",
};

/** Format an ability/skill modifier with an explicit sign. */
export function signed(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`;
}

/** Convert an ability score (1–30) to its modifier. */
export function abilityMod(score: number): number {
  return Math.floor((score - 10) / 2);
}

/** Title-case a slug like "hill-dwarf" → "Hill Dwarf". */
export function titleizeSlug(slug: string | null | undefined): string {
  if (!slug) return "";
  return slug
    .split(/[-_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Format a unix timestamp (seconds) as a short clock time. */
export function clockTime(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Format a unix timestamp (seconds) as a friendly relative-ish date. */
export function friendlyDate(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Format a challenge rating: 0.25 → "1/4". */
export function formatCR(cr: number | null | undefined): string {
  if (cr === null || cr === undefined) return "—";
  const fractions: Record<string, string> = {
    "0.125": "1/8",
    "0.25": "1/4",
    "0.5": "1/2",
  };
  const key = String(cr);
  if (fractions[key]) return fractions[key];
  return String(cr);
}

export function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** A stable-ish unique id for client-side stream items. */
let _uid = 0;
export function uid(prefix = "u"): string {
  _uid += 1;
  return `${prefix}_${Date.now().toString(36)}_${_uid}`;
}

/** Proficiency bonus by total level (5e standard progression). */
export function profBonusForLevel(level: number): number {
  return Math.floor((level - 1) / 4) + 2;
}

/** Pull a readable string out of an arbitrary SRD field. */
export function asText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(asText).join(", ");
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if ("name" in o) return String(o.name);
    if ("index" in o) return String(o.index);
  }
  return "";
}
